import { PDFDownloadLink } from "@react-pdf/renderer";
import { selectIsPremium } from "domain/auth/auth.selectors";
import { Technique, TechniqueType } from "domain/entities/techniques.entities";
import {
  HiCheck,
  HiHeart,
  HiOutlineHeart,
  HiOutlinePencil,
} from "react-icons/hi";
import { BannerGrey400 } from "ui/assets/svg/technique-banners-desktop/BannerGrey400";
import { BannerLightGreen400 } from "ui/assets/svg/technique-banners-desktop/BannerLightGreen400";
import { BannerLightGreen500 } from "ui/assets/svg/technique-banners-desktop/BannerLightGreen500";
import { BannerPaleGreen200 } from "ui/assets/svg/technique-banners-desktop/BannerPaleGreen200";
import { BannerPaleGreen400 } from "ui/assets/svg/technique-banners-desktop/BannerPaleGreen400";
import { BannerPink300 } from "ui/assets/svg/technique-banners-desktop/BannerPink300";
import { BannerPink400 } from "ui/assets/svg/technique-banners-desktop/BannerPink400";
import { CardBgGrey400 } from "ui/assets/svg/technique-card-backgrounds/CardBgGrey400";
import { CardBgLightGreen400 } from "ui/assets/svg/technique-card-backgrounds/CardBgLightGreen400";
import { CardBgLightGreen500 } from "ui/assets/svg/technique-card-backgrounds/CardBgLightGreen500";
import { CardBgPaleGreen200 } from "ui/assets/svg/technique-card-backgrounds/CardBgPaleGreen200";
import { CardBgPaleGreen400 } from "ui/assets/svg/technique-card-backgrounds/CardBgPaleGreen400";
import { CardBgPink300 } from "ui/assets/svg/technique-card-backgrounds/CardBgPink300";
import { CardBgPink400 } from "ui/assets/svg/technique-card-backgrounds/CardBgPink400";
import { useAppSelector } from "ui/hook/store";
import { useWindowWidth } from "ui/utils/hooks";
import { TechniquePDF } from "ui/pages/app/techniques/technique/TechniquePDF";
import { selectTechniqueAdvice } from "domain/techniques/techniques.selectors";
import { DownloadActivityTooltip } from "ui/pages/app/components/activities/DownloadActivityTooltip";
import { DownloadImageIcon } from "ui/assets/svg/icons/DownloadImageIcon";
import { DownloadActivityImagesTooltip } from "ui/pages/app/components/activities/techniques/DownloadActivityImagesTooltip";
import { CustomTextTooltip } from "ui/pages/app/components/CustomTextTooltip";
import { translate } from "ui/i18n";
import { selectOnBoardingValues } from "domain/on-boarding/on-boarding.selectors";
import { PDFIcon } from "ui/assets/svg/icons/PDFIcon";

export function TechniqueBanner({
  technique,
  checkIcon,
  selected,
  isFavorite,
  handleFavoriteClick,
  handleDownloadClick,
  handleDownloadPDFClick,
  onEdit,
  handleDownloadImagesClick,
}: {
  technique: Technique;
  checkIcon?: boolean;
  selected?: boolean;
  isFavorite?: boolean;
  handleFavoriteClick?: (e: any) => Promise<void>;
  handleDownloadClick?: (e: any) => Promise<void>;
  handleDownloadPDFClick?: (e: any) => void;
  onEdit?: () => void;
  handleDownloadImagesClick?: (e: any) => void;
}) {
  const isDesktop = useWindowWidth();
  const isPremium = useAppSelector(selectIsPremium);
  const advice = useAppSelector(selectTechniqueAdvice);
  const onBoardingValues = useAppSelector(selectOnBoardingValues);

  const bannerInfos = getBannerImage(
    technique.type as TechniqueType,
    isDesktop
  );
  return (
    <div className="activity-banner relative w-full">
      <bannerInfos.image width="100%" height="100%" />
      {isDesktop && !checkIcon && (
        <div className="absolute right-6 top-6 hidden space-x-4 md:flex">
          {handleFavoriteClick && typeof isFavorite === "boolean" && (
            <CustomTextTooltip
              text={
                isFavorite
                  ? translate("ns1:Activities.Tooltips.RemoveFromFavorites")
                  : translate("ns1:Activities.Tooltips.AddToFavorites")
              }
            >
              <button
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                onClick={handleFavoriteClick}
              >
                {isFavorite ? (
                  <HiHeart className="text-secondaryGrey-900" size={20} />
                ) : (
                  <HiOutlineHeart
                    className="text-secondaryGrey-900"
                    size={20}
                  />
                )}
              </button>
            </CustomTextTooltip>
          )}

          {technique?.consignesCount > 0 && (
            <DownloadActivityImagesTooltip activity="technique">
              <button
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                onClick={handleDownloadImagesClick}
              >
                <DownloadImageIcon color="#2b3674" />
              </button>
            </DownloadActivityImagesTooltip>
          )}

          {isPremium ? (
            <PDFDownloadLink
              document={<TechniquePDF technique={technique} advice={advice} />}
              fileName={`${technique.title}.pdf`}
            >
              {onBoardingValues.closedDownloadActivityImages ? (
                <DownloadActivityTooltip type="technique">
                  <button
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                    onClick={handleDownloadPDFClick}
                  >
                    <PDFIcon className="text-secondaryGrey-900" size={20} />
                  </button>
                </DownloadActivityTooltip>
              ) : (
                <CustomTextTooltip
                  text={translate("ns1:Activities.Tooltips.DownloadPDF")}
                >
                  <button
                    className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                    onClick={handleDownloadPDFClick}
                  >
                    <PDFIcon className="text-secondaryGrey-900" size={20} />
                  </button>
                </CustomTextTooltip>
              )}
            </PDFDownloadLink>
          ) : (
            <DownloadActivityTooltip type="technique">
              <button
                className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
                onClick={(e) =>
                  handleDownloadClick ? handleDownloadClick(e) : null
                }
              >
                <PDFIcon className="text-secondaryGrey-900" size={20} />
              </button>
            </DownloadActivityTooltip>
          )}
        </div>
      )}
      {isDesktop && checkIcon && (
        <div className="absolute right-6 top-6 hidden space-x-4 md:flex">
          {selected && (
            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-darkGrey-900 bg-darkGrey-700">
              <HiCheck color="#fff" size={26} />
            </div>
          )}
          {onEdit && (
            <button
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-white"
              onClick={onEdit}
            >
              <HiOutlinePencil className="text-secondaryGrey-900" size={20} />
            </button>
          )}
        </div>
      )}
      <div
        className={`items-bottom absolute bottom-6 flex w-full space-x-6 px-6 text-2xl ${bannerInfos.color}`}
      >
        <span className="font-bold">{technique.title}</span>
        {/* <span className="flex items-end">
          <HiThumbUp size={28} className="me-2" />
          <span className="font-medium">12</span>
        </span> */}
      </div>
    </div>
  );
}

export function getBannerImage(
  activityType: TechniqueType,
  isDesktop: boolean
) {
  switch (activityType) {
    case "ice_breaker":
      return {
        image: isDesktop ? BannerLightGreen500 : CardBgLightGreen500,
        color: "text-secondaryGrey-900",
      };
    case "equipes":
      return {
        image: isDesktop ? BannerPaleGreen400 : CardBgPaleGreen400,
        color: "text-white",
      };
    case "brainstorming":
      return {
        image: isDesktop ? BannerPink300 : CardBgPink300,
        color: "text-secondaryGrey-900",
      };
    case "projects":
      return {
        image: isDesktop ? BannerPaleGreen200 : CardBgPaleGreen200,
        color: "text-secondaryGrey-900",
      };
    case "evaluation":
      return {
        image: isDesktop ? BannerPink400 : CardBgPink400,
        color: "text-white",
      };
    case "pause":
      return {
        image: isDesktop ? BannerLightGreen400 : CardBgLightGreen400,
        color: "text-secondaryGrey-900",
      };
    case "vote":
      return {
        image: isDesktop ? BannerGrey400 : CardBgGrey400,
        color: "text-white",
      };
    default:
      return {
        image: isDesktop ? BannerPink400 : CardBgPink400,
        color: "text-secondaryGrey-900",
      };
  }
}
